import { createApp } from 'vue'
import { startAppUpdates } from './services/appUpdateService'
import App from './App.vue'
import router from './router'
import { bootstrapI18n } from './services/i18nService'
import { initWhiteLabel } from './services/brandingService'
import { performImpersonateLogin, clearAccessToken, bootstrapSession } from './services/authService'
import { initSentry } from './services/sentryService'
import { initTheme } from './services/themeService'
import { initNavLayout } from './services/navLayoutService'
import { initTextSize } from './services/textSizeService'
import { initMobilePanel } from './services/mobilePanelService'
import './style.css'

// Новая версия портала после деплоя: service worker регистрируем сами и применяем
// обновление только в безопасный момент (открытие, переход на страницу, долгий фон),
// а не посреди работы. Подробности в services/appUpdateService.js
startAppUpdates(router)

// === Авто-восстановление после рассинхрона ассетов ===
// При деплоє меняются хеши чанков. Вкладка со старым index.html ловит 404 на старые
// чанки → "Failed to fetch dynamically imported module" и страница падает (классика
// SPA+PWA). Делаем одноразовый reload, чтобы подтянуть свежий index.html с правильными
// хешами. Защита от петли: не чаще одного раза в 15 c (sessionStorage).
const STALE_CHUNK_RE = /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|dynamically imported module/i
function reloadOnceForStaleChunks(reason) {
  try {
    const KEY = 'cloudgps:chunkReloadAt'
    const last = Number(sessionStorage.getItem(KEY) || 0)
    const now = Date.now()
    if (now - last < 15000) return // уже перезагружались только что — не зацикливаемся
    sessionStorage.setItem(KEY, String(now))
  } catch (_) { /* sessionStorage может быть недоступен (приватный режим) */ }
  console.warn('[chunk-reload] stale asset detected, reloading:', reason)
  window.location.reload()
}
// Официальное событие Vite при провале preload динамического чанка.
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault?.()
  reloadOnceForStaleChunks('vite:preloadError')
})
// Падение ленивого импорта на уровне роутера (переход на страницу).
router.onError((error) => {
  if (STALE_CHUNK_RE.test(error?.message || '')) reloadOnceForStaleChunks('router.onError')
})

// === Полифилл canvas roundRect ===
// ctx.roundRect появился только в Safari 16.4 / Chrome 99. На iPhone, которые застряли
// на iOS 15 (6s/7/SE1), плагины chart.js падали на первом же бейдже заправки/слива —
// и вместе с ними умирал ВЕСЬ рендер графика маршрута (Sentry VUE-40, ошибка сыпалась
// каждый кадр). Ставим до монтирования приложения, чтобы покрыть все 5 мест отрисовки.
if (typeof CanvasRenderingContext2D !== 'undefined'
  && typeof CanvasRenderingContext2D.prototype.roundRect !== 'function') {
  CanvasRenderingContext2D.prototype.roundRect = function roundRect(x, y, w, h, r) {
    // Спека принимает число или массив радиусов; у нас везде одно число.
    const raw = Array.isArray(r) ? (r[0] ?? 0) : (r ?? 0)
    const radius = Math.min(Math.abs(Number(raw) || 0), Math.abs(w) / 2, Math.abs(h) / 2)
    this.moveTo(x + radius, y)
    this.arcTo(x + w, y, x + w, y + h, radius)
    this.arcTo(x + w, y + h, x, y + h, radius)
    this.arcTo(x, y + h, x, y, radius)
    this.arcTo(x, y, x + w, y, radius)
    this.closePath()
    return this
  }
}

// Применяем тему и подписываемся на смену системной темы (для auto-режима).
// Inline-скрипт в index.html уже выставил data-theme до загрузки CSS;
// здесь — реактивная часть и слушатели на смены prefers-color-scheme.
initTheme()
// Режим главного меню (topbar|side|side-icons) - атрибут data-nav на <html>
initNavLayout()
// Размер текста (normal|large|xlarge) - атрибут data-text-size на <html>
initTextSize()
// Ширина колонки списка на телефоне (drawer-режим) - переменная --mobile-drawer-width на <html>
initMobilePanel()

// Обрабатываем impersonate токен из URL параметра, если он присутствует
async function handleImpersonateToken() {
  const urlParams = new URLSearchParams(window.location.search)
  const impersonateToken = urlParams.get('impersonate')
  const redirectPath = urlParams.get('redirect') // Путь для перенаправления после авторизации

  if (impersonateToken) {
    try {
      // Очищаем текущую сессию перед входом через impersonate
      clearAccessToken()
      await performImpersonateLogin(impersonateToken)
      // Если есть redirect, сохраняем его для обработки после монтирования роутера
      if (redirectPath) {
        sessionStorage.setItem('impersonate_redirect', redirectPath)
      }
      // Убираем токен из URL, чтобы он не остался в истории браузера
      const cleanUrl = window.location.origin + window.location.pathname
      window.history.replaceState({}, document.title, cleanUrl)
    } catch (error) {
      console.error('Impersonate login failed:', error)
      // При ошибке убираем параметр из URL и перенаправляем на логин
      const cleanUrl = window.location.origin + window.location.pathname
      window.history.replaceState({}, document.title, cleanUrl)
    }
  }
}

// Монтируем приложение сразу, i18n загружается в фоне (не блокирует рендеринг)
async function initApp() {
  // Обрабатываем impersonate токен если есть
  await handleImpersonateToken()

  // Відновлюємо сесію через refresh-cookie (якщо є валідний refresh-токен у браузера).
  // Чекаємо перед mount, щоб router beforeEach одразу бачив правильний auth-стан
  // і не редиректив залогіненого користувача на /login.
  await bootstrapSession()

  const app = createApp(App)
  app.use(router)

  // Инициализируем Sentry для отслеживания ошибок (если настроен VITE_SENTRY_DSN)
  await initSentry(app, router)

  // Монтируем приложение сразу - покажет loader пока грузится i18n
  app.mount('#app')

  // White label: на партнёрских доменах тянем бренд с сервера (не блокирует
  // старт; до ответа работает localStorage-кэш из inline-скрипта index.html)
  initWhiteLabel().catch(() => { /* офлайн — остаёмся на кэше/дефолте */ })

  // Загружаем i18n в фоне - App.vue отслеживает isI18nReady()
  bootstrapI18n().catch((error) => {
    console.error('Не удалось инициализировать клиентский i18n', error)
  })
}

initApp()
